import { useCallback, useId, useMemo, useState } from "react";

import { Dialog } from "~/components/Dialog";
import {
  DialogFooter,
  DialogHeader,
  ErrorLine,
  Segmented,
  TextInput,
} from "~/components/CreateRoomDialog";
import { GhostButton, PrimaryButton } from "~/components/MessageButtons";
import { useMatrix, useMatrixRooms } from "~/context/MatrixContext";

interface CreateSpaceDialogProps {
  onClose: () => void;
}

export function CreateSpaceDialog({ onClose }: CreateSpaceDialogProps) {
  const { createSpace, activeOrgSpaceId } = useMatrix();
  const rooms = useMatrixRooms();
  const [name, setName] = useState("");
  const [topic, setTopic] = useState("");
  const [isPrivate, setIsPrivate] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const titleId = `${useId()}-title`;
  const nameId = `${useId()}-name`;
  const topicId = `${useId()}-topic`;

  const parentSpace = useMemo(
    () => rooms.find((r) => r.id === activeOrgSpaceId && r.isSpace) ?? null,
    [rooms, activeOrgSpaceId],
  );
  const parentName = parentSpace?.name || "Space";

  const requestClose = useCallback(() => {
    if (submitting) return;
    onClose();
  }, [submitting, onClose]);

  const canSubmit = name.trim().length > 0 && !submitting;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      if (!activeOrgSpaceId) {
        setError("No space selected. Pick a space in the top bar first.");
        return;
      }
      await createSpace({
        name: name.trim(),
        topic: topic.trim() || undefined,
        parentSpaceId: activeOrgSpaceId,
        isPrivate,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't create the space.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog onClose={requestClose} labelledBy={titleId}>
      <DialogHeader
        title={`Create space in ${parentName}`}
        titleId={titleId}
        onClose={requestClose}
      />
      <div
        style={{
          padding: "var(--chat-dialog-body-padding)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--chat-space-4)",
          overflowY: "auto",
        }}
      >
        <div>
          <label
            htmlFor={nameId}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--chat-space-1)",
              marginBottom: "var(--chat-space-1)",
              color: "var(--chat-fg-secondary)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-sm-size)",
              lineHeight: "var(--chat-type-body-sm-line)",
            }}
          >
            <span>Name</span>
            <span
              aria-hidden
              style={{
                display: "inline-block",
                width: 4,
                height: 4,
                borderRadius: "var(--chat-radius-full)",
                background: "var(--chat-fg-muted)",
              }}
            />
          </label>
          <TextInput
            id={nameId}
            value={name}
            onChange={setName}
            placeholder="e.g. Design, Engineering"
          />
        </div>
        <div>
          <label
            htmlFor={topicId}
            style={{
              display: "block",
              marginBottom: "var(--chat-space-1)",
              color: "var(--chat-fg-secondary)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-sm-size)",
              lineHeight: "var(--chat-type-body-sm-line)",
            }}
          >
            Topic
          </label>
          <TextInput id={topicId} value={topic} onChange={setTopic} placeholder="Optional" />
        </div>
        <div>
          <div
            style={{
              marginBottom: "var(--chat-space-1)",
              color: "var(--chat-fg-secondary)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-sm-size)",
              lineHeight: "var(--chat-type-body-sm-line)",
            }}
          >
            Visibility
          </div>
          <Segmented
            value={isPrivate ? "private" : "org"}
            onChange={(v) => setIsPrivate(v === "private")}
            options={[
              { value: "private", label: "Private" },
              { value: "org", label: `Visible to ${parentName}` },
            ]}
          />
        </div>
        {error && <ErrorLine>{error}</ErrorLine>}
      </div>
      <DialogFooter>
        <GhostButton onClick={requestClose} disabled={submitting}>
          Cancel
        </GhostButton>
        <PrimaryButton onClick={() => void handleSubmit()} disabled={!canSubmit}>
          {submitting ? "Creating…" : "Create"}
        </PrimaryButton>
      </DialogFooter>
    </Dialog>
  );
}
